import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, withCtx as _withCtx, createVNode as _createVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, unref as _unref } from "vue"

const _hoisted_1 = { class: "project-offered" }
const _hoisted_2 = { class: "table__footer" }
const _hoisted_3 = {
  key: 1,
  class: "empty"
}
const _hoisted_4 = { class: "bottom-buttons" }

import { computed, ref, reactive, watch } from "vue";
import { useQuery } from "@vue/apollo-composable";
import { useI18n } from "vue-i18n";
import { type PaginatedResult } from "@/helpers/typesDefinition";
import { type Offered } from "@/helpers/project/typesDefinition";
import { formatPrice } from "@/helpers/utils";
import TableBase, { type TableBaseColumn } from "@/components/TableBase.vue";
import BasicButton from "@/components/BasicButton.vue";
import checkHasAccess from "@/helpers/permissions/checkHasAccess";
import { GET_PROJECT_FOR_ACCESS_RIGHTS } from "@/graphql/project/queries";
import { useToast } from "vue-toastification";
import { GET_PROJECT_OFFERED } from "@/graphql/offered/queries";
import OfferedDetails from "@/components/offered/OfferedDetails.vue";
import AddOffered from "@/components/offered/AddOffered.vue";

type GetProjectOfferedResult = {
  projectOffered: PaginatedResult<Offered>;
};


export default /*@__PURE__*/_defineComponent({
  __name: 'ProjectOffered',
  props: {
    id: {},
    offeredId: {}
  },
  setup(__props: any) {

const props = __props;

const { t } = useI18n({
  inheritLocale: true,
  useScope: "global",
});

const toast = useToast();

const getProjectOfferedVariables = computed(() => {
  const projectId = props.id;
  if (projectId) {
    return {
      offset: 0,
      projectId,
    };
  }
  return {};
});

const { result: offeredResult, fetchMore } = useQuery<GetProjectOfferedResult>(
  GET_PROJECT_OFFERED,
  getProjectOfferedVariables,
);

const refetchQueries = computed(() => [
  {
    query: GET_PROJECT_OFFERED,
    variables: getProjectOfferedVariables.value,
  },
]);

const offered = computed(() => {
  return offeredResult.value?.projectOffered.items;
});

const hasMore = computed(() => {
  return offeredResult.value?.projectOffered?.hasMore;
});

const { result: projectResult } = useQuery(GET_PROJECT_FOR_ACCESS_RIGHTS, {
  id: props.id,
});

const loadMore = () => {
  if (hasMore.value) {
    fetchMore({
      variables: {
        projectId: props.id,
        offset: offered.value?.length,
      },
    });
  }
};

const hasAdminAccess = ref(false);
const hasClientAccess = ref(false);
const readOnly = ref(true);

watch([projectResult], async ([val]) => {
  const project = val?.project;
  if (!project) return;

  readOnly.value = project.active;

  hasAdminAccess.value = await checkHasAccess(
    project.client.id,
    props.id,
    "admin",
    toast,
    t("errors.getUserDetails"),
  );

  hasClientAccess.value = await checkHasAccess(
    project.client.id,
    props.id,
    "client",
    toast,
    t("errors.getUserDetails"),
  );
});

const columns: TableBaseColumn[] = [
  {
    name: "amountNet",
    title: t("labels.offered.amountNet"),
  },
  {
    name: "date",
    title: t("labels.offered.date"),
  },
  {
    name: "comment",
    title: t("labels.offered.comment"),
  },
];

const references = reactive<Record<string, HTMLElement | null>>({});

const getRef = (offeredId: string, element: HTMLElement | null) => {
  references[offeredId] = element;
};

const formattedOffered = computed(() => {
  const raw = offeredResult.value?.projectOffered.items || [];

  return raw.map((item) => ({
    ...item,
    amountNet: formatPrice(item.amountNet),
    comment: item.comment || "-",
  }));
});

const modalOfferedId = ref(props.offeredId ?? "");

const isModalDetailsVisible = ref(false);
function showModalDetails(): void {
  isModalDetailsVisible.value = true;
}

function closeOfferedDetailsModal(): void {
  isModalDetailsVisible.value = false;
}

const isAddOfferedModalVisible = ref(false);
function showAddOfferedModal(): void {
  isAddOfferedModalVisible.value = true;
}

function closeAddOfferedModal(): void {
  isAddOfferedModalVisible.value = false;
}

const selectOffered = (offeredId: string) => {
  modalOfferedId.value = offeredId;
};

const fullSelectedOffered = computed<Offered | null>(() => {
  if (!offered.value) return null;
  return offered.value.find((item) => item.id === modalOfferedId.value) ?? null;
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(TableBase, {
      columns: columns,
      items: formattedOffered.value,
      onSelectItem: selectOffered,
      onClick: showModalDetails,
      "is-clickable": true
    }, {
      actions: _withCtx(({ item: offered }) => [
        _createElementVNode("div", {
          class: "cell cell--actions",
          ref: (el) => getRef(offered.id, el as HTMLElement | null)
        }, null, 512)
      ]),
      _: 1
    }, 8, ["items"]),
    _createElementVNode("div", _hoisted_2, [
      (hasMore.value)
        ? (_openBlock(), _createBlock(BasicButton, {
            key: 0,
            onClick: loadMore
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_ctx.$t("buttons.offered.loadMore")), 1)
            ]),
            _: 1
          }))
        : (_openBlock(), _createElementBlock("p", _hoisted_3, _toDisplayString(_ctx.$t("offered.allLoaded")), 1))
    ]),
    (isModalDetailsVisible.value && fullSelectedOffered.value)
      ? (_openBlock(), _createBlock(OfferedDetails, {
          key: 0,
          id: modalOfferedId.value,
          onClose: _cache[0] || (_cache[0] = ($event: any) => (closeOfferedDetailsModal())),
          project: _unref(projectResult).project,
          offered: fullSelectedOffered.value,
          "has-client-access": hasClientAccess.value,
          hasAdminAccess: hasAdminAccess.value,
          refetchQueries: refetchQueries.value
        }, null, 8, ["id", "project", "offered", "has-client-access", "hasAdminAccess", "refetchQueries"]))
      : _createCommentVNode("", true),
    (
        _unref(projectResult) &&
        _unref(projectResult).project.active &&
        hasAdminAccess.value &&
        isAddOfferedModalVisible.value
      )
      ? (_openBlock(), _createBlock(AddOffered, {
          key: 1,
          onClose: _cache[1] || (_cache[1] = ($event: any) => (closeAddOfferedModal())),
          "project-id": _unref(projectResult).project.id,
          refetchQueries: refetchQueries.value
        }, null, 8, ["project-id", "refetchQueries"]))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_4, [
      (hasAdminAccess.value)
        ? (_openBlock(), _createBlock(BasicButton, {
            key: 0,
            onClick: _cache[2] || (_cache[2] = ($event: any) => (showAddOfferedModal())),
            class: "add-button"
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_unref(t)("buttons.offered.add")), 1)
            ]),
            _: 1
          }))
        : _createCommentVNode("", true)
    ])
  ]))
}
}

})